import { useState, useEffect, useCallback } from "react";
import { getMsg } from "../../utils/antdStatic";
import { apiFetch } from "../../services/apiClient";
import type { Dayjs } from "dayjs";
import { ahoraChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import {
  CADUCIDAD_FECHAS_DIAS,
  deserializarDia,
  serializarDia,
} from "../../hooks/filtrosPersistidos";

export interface AseoResumen {
  id: number;
  /** Código de estadía AF del arriendo vinculado (reservaId), o null (manual). */
  reservaId: number | null;
  estado: string;
  urgencia: string;
  costoLimpieza: number;
  sueldoAseador: number;
  horaAseo: string;
  observaciones: string;
  aseador: { id: number; nombre: string } | null;
  propiedad: { id: number; nombre: string } | null;
}

interface CalendarItem {
  id: number;
  reservaId: number | null;
  estado: string;
  urgencia: string;
  fecha: string;
  fechaFin: string;
  horaInicio: string;
  costoLimpieza: number;
  sueldoAseador: number;
  observaciones: string;
  aseador: { id: number; nombre: string } | null;
  propiedad: { id: number; nombre: string } | null;
}

interface CalendarResponse {
  success: boolean;
  count: number;
  data: CalendarItem[];
}

export const useAseosCalendarLogic = () => {
  // El mes que el admin estaba mirando se recuerda, y caduca a los 7 días:
  // es el único filtro de esta vista que pega al backend, así que abrir en un
  // mes viejo además de desorientar trae datos que no son los de hoy. Dayjs no
  // sobrevive a JSON → se guarda "YYYY-MM-DD".
  const [currentMonth, setCurrentMonth] = useFiltroPersistido<Dayjs>(
    "aseos.mesCalendario",
    () => ahoraChile(),
    {
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      serializar: serializarDia,
      // `null` no es un valor posible acá: el calendario siempre tiene un mes.
      deserializar: (crudo) => deserializarDia(crudo) ?? undefined,
    },
  );
  const [aseosByDate, setAseosByDate] = useState<Map<string, AseoResumen[]>>(
    new Map()
  );
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const fetchAseos = useCallback(async () => {
    const from = currentMonth.startOf("month").format("YYYY-MM-DD");
    const to = currentMonth.endOf("month").format("YYYY-MM-DD");

    setLoading(true);
    try {
      const res = await apiFetch<CalendarResponse>(
        `/aseos/calendario?from=${from}&to=${to}`
      );

      const map = new Map<string, AseoResumen[]>();
      if (res?.data) {
        for (const c of res.data) {
          const dateKey = c.fecha ?? from;

          const resumen: AseoResumen = {
            id: c.id,
            reservaId: c.reservaId ?? null,
            estado: c.estado,
            urgencia: c.urgencia,
            costoLimpieza: c.costoLimpieza,
            sueldoAseador: c.sueldoAseador,
            horaAseo: c.horaInicio,
            observaciones: c.observaciones,
            // Round 2026-08-25 — equipo 1..N: el nombre mostrado une a todas
            // ("Ana + Rosa"); el shape {id, nombre} se preserva para compat.
            aseador: (() => {
              const nombres = (Array.isArray((c as any).aseadores)
                ? (c as any).aseadores
                : []
              )
                .map((x: any) => x?.nombre)
                .filter(Boolean);
              if (nombres.length > 1 && c.aseador) {
                return { ...c.aseador, nombre: nombres.join(" + ") };
              }
              return c.aseador;
            })(),
            propiedad: c.propiedad,
          };

          const existing = map.get(dateKey) ?? [];
          existing.push(resumen);
          map.set(dateKey, existing);
        }
      }
      setAseosByDate(map);
    } catch (err) {
      console.error("Error fetching aseos calendar:", err);
      getMsg()?.error("Error al cargar el calendario de aseos");
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchAseos();
  }, [fetchAseos]);

  const handleMonthChange = (date: Dayjs) => {
    setCurrentMonth(date);
    setSelectedDate(null);
  };

  const aseosForDate = selectedDate
    ? aseosByDate.get(selectedDate) ?? []
    : [];

  const getCountForDate = (date: string) => {
    return aseosByDate.get(date)?.length ?? 0;
  };

  return {
    currentMonth,
    handleMonthChange,
    aseosByDate,
    loading,
    selectedDate,
    setSelectedDate,
    aseosForDate,
    getCountForDate,
  };
};
